import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { useNotifications } from "@/context/NotificationsContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bell, FileText, ImageIcon, CheckCheck, Loader2, ArrowRight } from "lucide-react";

const TYPE_ICON = { document: FileText, photo: ImageIcon, report: FileText };

function timeAgo(iso) {
  const d = new Date(iso);
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return "acum";
  if (secs < 3600) return `${Math.floor(secs / 60)} min`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} ore`;
  return `${Math.floor(secs / 86400)} zile`;
}

export default function Notifications() {
  const [items, setItems] = useState(null);
  const { refresh } = useNotifications();
  const { user } = useAuth();
  const variant = user?.role === "client" ? "client" : "staff";

  async function load() {
    const { data } = await api.get("/notifications");
    setItems(data);
    refresh();
  }

  useEffect(() => {
    load();
  }, []);

  async function markAll() {
    try {
      await api.post("/notifications/read-all");
      toast.success("Toate notificările marcate ca citite");
      await load();
    } catch {
      toast.error("Eroare");
    }
  }

  async function markOne(id) {
    try {
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch (_) { /* ignore */ }
  }

  return (
    <PortalLayout variant={variant}>
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="section-eyebrow">Activitate</div>
            <h1 className="font-display font-semibold text-[42px] md:text-[48px] tracking-[-0.02em] leading-tight text-ink">Notificări</h1>
          </div>
          {items?.some((n) => !n.read) && (
            <button data-testid="mark-all-read" onClick={markAll} className="flex items-center gap-2 px-4 py-2 border border-neutral-200 hover:border-brand hover:text-brand text-sm font-medium transition-colors">
              <CheckCheck className="h-4 w-4" /> Marchează toate
            </button>
          )}
        </div>

        {items === null ? (
          <div className="flex items-center justify-center py-24 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-neutral-300 bg-white">
            <Bell className="h-8 w-8 text-ink-muted mx-auto mb-3" />
            <p className="text-ink-muted">Nicio notificare încă.</p>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="notifications-list">
            {items.map((n) => {
              const Icon = TYPE_ICON[n.type] || Bell;
              return (
                <div key={n.id} className={`flex items-start gap-4 px-6 py-5 transition-colors ${n.read ? "" : "bg-brand/[0.04]"}`}>
                  <div className={`h-10 w-10 flex items-center justify-center shrink-0 ${n.read ? "bg-neutral-100 text-ink-muted" : "bg-brand/10 text-brand"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink">{n.message}</div>
                    <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-ink-muted">{timeAgo(n.created_at)} în urmă</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!n.read && (
                      <button data-testid={`notif-read-${n.id}`} onClick={() => markOne(n.id)} className="text-xs text-ink-muted hover:text-brand">
                        Marchează
                      </button>
                    )}
                    {n.project_id && (
                      <Link to={`/portal/proiecte/${n.project_id}`} className="text-brand hover:text-brand-hover">
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
